import { StyleSheet, css } from 'aphrodite';
import { type FC, useEffect, useMemo, useState } from 'react';
import { Button } from '../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../web/components-v2/Dialog';
import { Input } from '../../../web/components-v2/Input';
import type { FocusedLeadContact } from '../data';
import { IconPlus, IconX } from '../ui/icons';

/**
 * Add / Edit Contact dialog for the LEFT column.
 *
 * The same component handles both `mode: 'add'` and `mode: 'edit'` because
 * the legacy contact form behaves identically for the two — only the title
 * changes and the submit verb. Pre-populates from `contact` when editing.
 *
 * The dialog is purely presentational: it gathers form state and hands a
 * `ContactDraft` back to the parent, which dispatches the mutation. No
 * react-query, no MobX in this file.
 */
export type ContactPhoneDraft = {
	value: string;
	label: string;
};

export type ContactDraft = {
	firstName: string;
	lastName: string;
	jobTitle: string;
	email: string;
	phones: ContactPhoneDraft[];
};

export type ContactDialogMode = 'add' | 'edit';

export type ContactDialogProps = {
	open: boolean;
	mode: ContactDialogMode;
	onOpenChange: (open: boolean) => void;
	contact?: FocusedLeadContact;
	onSave: (draft: ContactDraft) => void;
	saving?: boolean;
};

const PHONE_LABELS = ['Work', 'Mobile', 'Cell', 'Home', 'Direct', 'Office', 'Fax', 'Other'];
const DEFAULT_LABEL = 'Work';

const splitDisplayName = (name: string): { firstName: string; lastName: string } => {
	const parts = (name ?? '').trim().split(/\s+/);
	if (parts.length <= 1) {
		return { firstName: parts[0] ?? '', lastName: '' };
	}
	return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
};

const buildInitialDraft = (mode: ContactDialogMode, contact?: FocusedLeadContact): ContactDraft => {
	if (mode === 'edit' && contact) {
		const { firstName: derivedFirst, lastName: derivedLast } = splitDisplayName(contact.name);
		return {
			firstName: contact.firstName ?? derivedFirst ?? '',
			lastName: contact.lastName ?? derivedLast ?? '',
			jobTitle: contact.title ?? '',
			email: contact.email ?? '',
			phones: contact.phones.length
				? contact.phones.map(p => ({ value: p.standard, label: p.label ?? DEFAULT_LABEL }))
				: [{ value: '', label: DEFAULT_LABEL }],
		};
	}
	return {
		firstName: '',
		lastName: '',
		jobTitle: '',
		email: '',
		phones: [{ value: '', label: DEFAULT_LABEL }],
	};
};

export const ContactDialog: FC<ContactDialogProps> = ({ open, mode, onOpenChange, contact, onSave, saving }) => {
	const [draft, setDraft] = useState<ContactDraft>(() => buildInitialDraft(mode, contact));

	// Re-seed the form whenever the dialog re-opens or switches contact.
	useEffect(() => {
		if (!open) return;
		setDraft(buildInitialDraft(mode, contact));
	}, [open, mode, contact]);

	const title = mode === 'edit' ? 'Edit contact' : 'Add contact';
	const submitLabel = mode === 'edit' ? 'Save' : 'Add';

	// Need at least one of: name, email, or phone — match legacy behavior
	// where the contact endpoint rejects fully blank submissions.
	const canSave = useMemo(() => {
		if (saving) return false;
		const hasName = !!(draft.firstName.trim() || draft.lastName.trim());
		const hasEmail = !!draft.email.trim();
		const hasPhone = draft.phones.some(p => p.value.trim().length > 0);
		return hasName || hasEmail || hasPhone;
	}, [draft, saving]);

	const updatePhone = (index: number, patch: Partial<ContactPhoneDraft>) => {
		setDraft(prev => ({
			...prev,
			phones: prev.phones.map((p, i) => (i === index ? { ...p, ...patch } : p)),
		}));
	};

	const removePhone = (index: number) => {
		setDraft(prev => ({
			...prev,
			phones:
				prev.phones.length === 1 ? [{ value: '', label: DEFAULT_LABEL }] : prev.phones.filter((_, i) => i !== index),
		}));
	};

	const addPhone = () => {
		setDraft(prev => ({ ...prev, phones: [...prev.phones, { value: '', label: DEFAULT_LABEL }] }));
	};

	const handleSave = () => {
		if (!canSave) return;
		// Trim everything before handing off; keep only phones with a non-empty value.
		const cleaned: ContactDraft = {
			firstName: draft.firstName.trim(),
			lastName: draft.lastName.trim(),
			jobTitle: draft.jobTitle.trim(),
			email: draft.email.trim(),
			phones: draft.phones.map(p => ({ value: p.value.trim(), label: p.label })).filter(p => p.value.length > 0),
		};
		onSave(cleaned);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
				</DialogHeader>
				<DialogBody>
					<div className={css(styles.row2)}>
						<div className={css(styles.field)}>
							<label htmlFor='focused-contact-first' className={css(styles.label)}>
								First name
							</label>
							<Input
								id='focused-contact-first'
								value={draft.firstName}
								onChange={e => setDraft(prev => ({ ...prev, firstName: e.target.value }))}
								disabled={saving}
								autoFocus={mode === 'add'}
							/>
						</div>
						<div className={css(styles.field)}>
							<label htmlFor='focused-contact-last' className={css(styles.label)}>
								Last name
							</label>
							<Input
								id='focused-contact-last'
								value={draft.lastName}
								onChange={e => setDraft(prev => ({ ...prev, lastName: e.target.value }))}
								disabled={saving}
							/>
						</div>
					</div>
					<div className={css(styles.field)}>
						<label htmlFor='focused-contact-title' className={css(styles.label)}>
							Title
						</label>
						<Input
							id='focused-contact-title'
							value={draft.jobTitle}
							onChange={e => setDraft(prev => ({ ...prev, jobTitle: e.target.value }))}
							disabled={saving}
						/>
					</div>
					<div className={css(styles.field)}>
						<label htmlFor='focused-contact-email' className={css(styles.label)}>
							Email
						</label>
						<Input
							id='focused-contact-email'
							type='email'
							value={draft.email}
							onChange={e => setDraft(prev => ({ ...prev, email: e.target.value }))}
							disabled={saving}
							placeholder='name@example.com'
						/>
					</div>
					<div className={css(styles.field)}>
						<span className={css(styles.label)}>Phone numbers</span>
						<div className={css(styles.phoneList)}>
							{draft.phones.map((phone, index) => (
								<div key={index} className={css(styles.phoneRow)}>
									<Input
										value={phone.value}
										onChange={e => updatePhone(index, { value: e.target.value })}
										placeholder='(555) 555-0142'
										disabled={saving}
										styles={[styles.phoneInput]}
									/>
									<select
										value={phone.label}
										onChange={e => updatePhone(index, { label: e.target.value })}
										disabled={saving}
										className={css(styles.select)}
										aria-label='Phone number label'
									>
										{PHONE_LABELS.map(l => (
											<option key={l} value={l}>
												{l}
											</option>
										))}
									</select>
									<button
										type='button'
										className={css(styles.removeBtn)}
										title='Remove phone number'
										aria-label='Remove phone number'
										onClick={() => removePhone(index)}
										disabled={saving}
									>
										<IconX size={14} />
									</button>
								</div>
							))}
						</div>
						<button type='button' className={css(styles.addBtn)} onClick={addPhone} disabled={saving}>
							<IconPlus size={14} />
							Add phone number
						</button>
					</div>
				</DialogBody>
				<DialogFooter>
					<Button variant='outline' onClick={() => onOpenChange(false)} disabled={saving}>
						Cancel
					</Button>
					<Button variant='primary' onClick={handleSave} disabled={!canSave}>
						{saving ? 'Saving…' : submitLabel}
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};

const styles = StyleSheet.create({
	field: {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
		marginBottom: 14,
	},
	row2: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: 12,
	},
	label: {
		fontSize: 12,
		fontWeight: 500,
		color: 'var(--ink-muted)',
	},
	phoneList: {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
	},
	phoneRow: {
		display: 'flex',
		gap: 6,
		alignItems: 'center',
	},
	phoneInput: {
		flex: 1,
		minWidth: 0,
	},
	select: {
		height: 36,
		padding: '0 8px',
		borderRadius: 6,
		border: '1px solid var(--border)',
		background: 'var(--bg)',
		color: 'var(--ink)',
		fontSize: 13,
	},
	removeBtn: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 32,
		height: 32,
		borderRadius: 6,
		border: '1px solid var(--border)',
		background: 'transparent',
		color: 'var(--ink-muted)',
		cursor: 'pointer',
		':hover': {
			color: 'var(--ink)',
			background: 'var(--hover)',
		},
		':disabled': {
			cursor: 'not-allowed',
			opacity: 0.5,
		},
	},
	addBtn: {
		alignSelf: 'flex-start',
		marginTop: 8,
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		padding: '4px 10px',
		borderRadius: 6,
		border: '1px solid var(--border)',
		background: 'transparent',
		color: 'var(--ink-muted)',
		fontSize: 12,
		cursor: 'pointer',
		':hover': {
			color: 'var(--ink)',
			background: 'var(--hover)',
		},
		':disabled': {
			cursor: 'not-allowed',
			opacity: 0.5,
		},
	},
});
